import { useCallback, useEffect, useState } from "react";
import { IpRestS } from "../../rest/ipRest-service";
import { RestS } from "../../rest/rest-service";
import { getLocalUser } from "../functions/utilities";

export type ZohoStatus =
  | "checking"
  | "not_connected"
  | "pending" // token exists, first sync hasn't run/reported yet
  | "healthy"
  | "broken"; // last sync failed, or hasn't succeeded in a long time

// No fixed product answer yet on what counts as "stale" for a daily sync —
// 2 days gives a day of slack over a missed run before flagging it broken.
const STALE_AFTER_MS = 2 * 24 * 60 * 60 * 1000;

interface UseZohoStatusOptions {
  // Overrides getLocalUser().companyId — see useZohoConnect for why.
  companyId?: number;
}

export function useZohoStatus({ companyId }: UseZohoStatusOptions = {}) {
  const [status, setStatus] = useState<ZohoStatus>("checking");
  const [accessTokenId, setAccessTokenId] = useState<number | null>(null);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  const refreshStatus = useCallback(() => {
    setStatus("checking");
    const user = getLocalUser();
    const resolvedCompanyId = companyId ?? user?.companyId;
    IpRestS.getIpByName("Zoho")
      .then((ip) => {
        if (!ip?._id || !resolvedCompanyId) throw new Error("missing ipId");
        return RestS.getAccessTokensByIp(resolvedCompanyId, ip._id);
      })
      .then((tokens) => {
        if (tokens.length === 0) {
          setAccessTokenId(null);
          setAccountName(null);
          setLastSyncedAt(null);
          setStatus("not_connected");
          return;
        }
        setAccessTokenId(tokens[0].id);
        setAccountName(tokens[0].customer_name || tokens[0].storeName || null);

        RestS.getDfStatusByIp("Zoho")
          .then((res: any) => {
            const statuses = res?.dfStatuses ?? [];
            if (!statuses.length) {
              setLastSyncedAt(null);
              setStatus("pending");
              return;
            }
            const latest = statuses.reduce((a: any, b: any) =>
              new Date(a.createdAt) > new Date(b.createdAt) ? a : b
            );
            const ageMs = Date.now() - new Date(latest.createdAt).getTime();
            const isFailed = latest.status === "FAILURE";
            setLastSyncedAt(latest.createdAt ?? null);
            setStatus(isFailed || ageMs > STALE_AFTER_MS ? "broken" : "healthy");
          })
          .catch(() => setStatus("pending"));
      })
      .catch(() => {
        setAccessTokenId(null);
        setAccountName(null);
        setLastSyncedAt(null);
        setStatus("not_connected");
      });
  }, [companyId]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  return { status, accessTokenId, accountName, lastSyncedAt, refreshStatus };
}
